import { ChangeDetectorRef, Component, ElementRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { generateTrigrams, htmlToText } from "@cf-shared/shared-util";
import { marked } from 'marked';
import { CsPage, NavController } from "providers/nav-controller";
import { Subscription } from "rxjs";
import { AuthService } from "services/auth.service";
import { LoggingService } from "services/logging.service";
import { TelemetryService } from "services/telemetry.service";
import { TextDbService, TextEntry } from "services/text-db.service";
import { ACTIVE_TEXT_SEGMENT, ENGLISH_TEXT_SEGMENT, tryHelpIdFromDocId } from "services/text-locale";
import { FaqPairingOutcome, FaqSection, pairEnglishSummaries, parseFaqSections } from "./faq-sections";
import { HelpSearchResult, searchHelpTexts } from "./help-search";
import { IHelpCardPage } from "pages/help-card-page/help-card-page-interface";


import { LL } from 'i18n/i18n.service';
import { SearchOperationTracker, TrackedSearchOperation } from 'util/search-operation-tracker';

interface HelpTextUIItem {
  helpText: TextEntry;
  trigrams: Set<string>;  // used for search
  plainText: string; // used for search;
}

type HelpSearchRow = HelpSearchResult<HelpTextUIItem>;

interface SearchOperation extends TrackedSearchOperation {
  foundItems: HelpSearchRow[];
}

@Component({
    selector: 'page-help',
    templateUrl: 'help-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpPage extends CsPage<{}> {
  protected LL = LL;

  protected showSpinner = true;
  protected searchDebouncerTimeout: ReturnType<typeof setTimeout> = undefined;
  protected prevSearchText = '';
  protected searchText = '';
  protected searchSubscription: Subscription;

  protected sourceHelpTexts: HelpTextUIItem[] = []; /** used for "copy from..." */
  protected filteredHelpTexts: HelpSearchRow[] = []; /** the current search's results, each with its preview */
  protected indexHelpText: TextEntry;
  protected faqSections: FaqSection[] = [];
  protected selectedFaqSection: FaqSection | null = null;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    protected cd: ChangeDetectorRef,
    private telemetry: TelemetryService,
    private textDb: TextDbService,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    void this.loadAsync();
    this.showSpinner = false;
  }

  async loadAsync() {
    // wrap so a getAllTextsAsync rejection (or any early throw before the per-text
    // loop's inner catch) surfaces via Sentry instead of silently swallowing through
    // ionViewCanEnter's void call, AND so the OnPush markForCheck still runs on the
    // error path — without finally, an early throw leaves the page stuck with
    // showSpinner=false, faqSections=[] (the #3510 symptom on the error branch).
    try {
      let allTexts = await this.textDb.getAllTextsAsync();
      this.sourceHelpTexts = [];
      for (let text of allTexts) {
        // compare the help id rather than the whole document id. The merge keeps
        // the English `_id` on every entry, so this would also work as a string
        // compare, but keying off the id itself is what that invariant rests on
        // — comparing the parsed help id stays correct however the list is
        // assembled. The non-throwing parse also keeps one malformed document
        // from aborting the loop.
        if (tryHelpIdFromDocId(text._id) === 'help.index') {
          this.indexHelpText = text;
          this.faqSections = parseFaqSections(text.text || '');
        }
        if (text.dontShowInHelpPage) {
          continue;
        }
        try {
          let plainText = htmlToText(marked(text.text || '', { async: false }));
          let titleTrigrams = generateTrigrams(text.title);
          this.sourceHelpTexts.push({ helpText: text, trigrams: titleTrigrams, plainText: plainText });
        } catch (err) {
          this.logging.trackSilentProblem('HelpTextParseFailure', {
            textId: text._id,
            errorMessage: err?.message,
          });
        }
      }
      // Pair the English FAQ headings without waiting for it. This is an extra
      // database read whose ONLY effect is which string gets reported to
      // analytics — `englishSummary` is never rendered — so nothing the user
      // sees depends on it, and it must not delay the list they are waiting for.
      //
      // It needs no markForCheck for the same reason, and it mutates the section
      // objects in place, so the already-rendered accordion stays valid. The one
      // cost: a tap in the moment before it finishes reports the translated
      // heading instead of the English one. That is a rare and small loss next to
      // holding up the whole page on a read nobody can see.
      void this.pairEnglishFaqHeadingsAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err);
    } finally {
      this.safeMarkForCheck(this.cd);
    }
  }

  /**
   * Give each parsed FAQ section its ENGLISH heading, so analytics stay
   * comparable across languages. The pairing rules live in `faq-sections.ts`;
   * this method only supplies the English document and reports the two ways it
   * can fail.
   */
  private async pairEnglishFaqHeadingsAsync() {
    // if the reader is already on English the headings ARE the English ones
    if (ACTIVE_TEXT_SEGMENT === ENGLISH_TEXT_SEGMENT) {
      return;
    }
    // if the index document was never found there is nothing to pair
    if (this.faqSections.length === 0) {
      return;
    }
    try {
      const englishIndex = await this.textDb.getTextByHelpIdAsync(
        'help.index', undefined, ENGLISH_TEXT_SEGMENT);
      const englishSections = parseFaqSections(englishIndex?.text || '');
      const outcome = pairEnglishSummaries(this.faqSections, englishSections);
      if (outcome === FaqPairingOutcome.CountMismatch) {
        this.logging.trackSilentProblem('HelpFaqSectionCountMismatch', {
          locale: ACTIVE_TEXT_SEGMENT,
          localizedCount: this.faqSections.length,
          englishCount: englishSections.length,
        });
      }
    } catch (err) {
      // a failed English read must not stop the page rendering, but it does mean
      // this session reports localized headings, so it is worth knowing about
      this.logging.trackSilentProblem('HelpFaqEnglishHeadingsUnavailable', {
        locale: ACTIVE_TEXT_SEGMENT,
        errorMessage: err?.message,
      });
    }
  }

  handleFaqSectionClick(section: FaqSection) {
    this.telemetry.trackEvent('help_page_faq_section_clicked', { section: section.englishSummary });
    this.selectedFaqSection = section;
    this.cd.markForCheck();
  }

  handleBackToFaqList() {
    this.selectedFaqSection = null;
    this.cd.markForCheck();
  }

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  isSearchInFocus() {
    let inputElement = this.searchInput.nativeElement as HTMLInputElement;
    if (inputElement) {
      return inputElement === document.activeElement;
    }
    return false;
  }

  handleSearchChanged() {
    /* Start showing spinner immediately when user starts typing, even though we start the search only after the debounce
     This makes the UI look more responsive. */
    this.showSpinner = true;
    this.cd.markForCheck();
    this.cd.detach();

    // debounce the input typing to prevent too many requests to the databases
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    // If we are deleting the search text
    if (this.searchText === '' && this.prevSearchText.length > 1) {
      this.telemetry.trackEvent('help_search_rejected', { search_text: this.prevSearchText });
    }
    this.prevSearchText = this.searchText;
    let text = this.searchText || '';
    this.searchDebouncerTimeout = setTimeout(() => {
      void this.searchAsync(text);
    }, 300);
  }

  private searchOps = new SearchOperationTracker<SearchOperation>();
  async searchAsync(searchText: string) {
    // separate into two scopes to prevent accidentally using untrimmed searchText
    // This can easily happen due to autocomplete and can cause subtle bugs that are hard to find.
    return this.searchAsync_internal(searchText.trim());
  }

  async searchAsync_internal(trimmedText: string) {
    let op = this.searchOps.start(trimmedText, { foundItems: [] });
    if (op === undefined) {
      return;
    }

    this.showSpinner = true;
    this.cd.markForCheck();
    this.cd.detach();

    if (this.searchOps.shouldCancel(op)) {
      this.searchOps.end(op);
      return;
    }

    // the matching and preview rules live in help-search.ts
    op.foundItems = searchHelpTexts(this.sourceHelpTexts, trimmedText);
    this.filteredHelpTexts = op.foundItems;

    this.showSpinner = false;
    this.cd.reattach();
    this.cd.markForCheck();
    this.searchOps.end(op);
    this.telemetry.trackEvent('help_search', { search_text: trimmedText, found_results: this.filteredHelpTexts.length });
  }

  handleHelpTextItemClick(item: HelpTextUIItem) {
    this.telemetry.trackEvent('help_page_item_clicked', { item: item.helpText._id });
    void this.navCtrl.pushAsync(IHelpCardPage, { helpId: item.helpText._id });
  }

}